/**
 * RequireVerification - Route guard for verified professionals
 * =============================================================
 * 
 * Purpose: Gate access to features requiring verification
 * Used for: Marketplace requests, integrations, exports, conflict checks
 * 
 * Design Decision: Shows gate message instead of redirecting (preserves URL for bookmarks)
 * 
 * Access Control Model:
 * - Unverified professional → Gate message with "Go Back" button
 * - Verified professional → Render children
 * - Loading state → Show spinner
 * 
 * Related: See PRO_ACCESS_CONTROL_MODEL.md for complete access matrix
 * 
 * v1.12.59 Fix: Changed from outlet context to useProProfile hook
 * - Previously relied on ProDashboardPage outlet context
 * - Failed on sibling routes like /pro/conflicts (not children of dashboard)
 * - Now self-sufficient, works on any /pro route
 */

import { ReactNode } from 'react';
import { useProProfile } from '@/hooks/useProProfile';

interface RequireVerificationProps {
  children: ReactNode;
}

export default function RequireVerification({ children }: RequireVerificationProps) {
  // Fetch professional profile directly (self-sufficient, no parent context needed)
  const { proProfile, loading, isVerified, isPending } = useProProfile();

  if (loading) {
    // Loading state while fetching professional profile
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  }

  // Gate access if not verified (includes missing profile, pending, or rejected)
  if (!isVerified) {
    // Show gate message (preserve URL for bookmarks)
    // isPending flag from hook already accounts for verification status
    
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
        <div className="mx-auto max-w-2xl rounded-lg border-2 border-orange-500/30 bg-slate-900 p-8 shadow-xl">
          <div className="mb-6 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-900/30">
              <svg className="h-10 w-10 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          
          <h2 className="mb-3 text-center text-2xl font-bold text-orange-200">
            Verification Required
          </h2>
          
          <p className="mb-6 text-center text-slate-300">
            This feature is only available to verified legal professionals. Your verification is currently{' '}
            <span className="font-semibold text-orange-300">
              {isPending ? 'pending review' : 'not yet submitted'}
            </span>.
          </p>

          <div className="mb-8 rounded-lg bg-slate-800/50 p-4 text-sm text-slate-400">
            <h3 className="mb-2 font-semibold text-slate-300">Why verification is required:</h3>
            <ul className="space-y-1.5">
              <li className="flex items-start">
                <span className="mr-2 text-orange-400">•</span>
                <span>Marketplace access and review request claims</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-orange-400">•</span>
                <span>Client data integrations and exports</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-orange-400">•</span>
                <span>Conflict checking and ethics compliance tools</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={() => window.history.back()}
              className="rounded-lg border border-slate-700 bg-slate-800 px-6 py-3 font-medium text-slate-300 transition-colors hover:bg-slate-700"
            >
              Go Back
            </button>
            <a
              href="/pro/setup"
              className="rounded-lg bg-orange-600 px-6 py-3 text-center font-medium text-white transition-colors hover:bg-orange-700"
            >
              {isPending ? 'View Verification Status' : 'Submit for Verification'}
            </a>
          </div>
          
          <p className="mt-6 text-center text-xs text-slate-500">
            Verification typically takes 1-2 business days after profile submission.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
